/**
 * ZipSuggestions — Iter-89.
 *
 * Lightweight, client-side ZIP autofill assist for resident-facing address
 * inputs. When both `city` and `state` are filled, this component queries
 * the OpenDataSoft public US-ZIP dataset (no API key, no CORS) and renders
 * 3–5 tappable ZIP chips. The user MUST tap a chip or keep typing — we
 * never auto-set the ZIP value (a wrong ZIP on a service contract is a
 * real problem, per CEO directive).
 *
 * Silent failure: if the lookup returns nothing, errors, or times out,
 * the component renders nothing. Never warns, never blocks the form.
 *
 * Props:
 *   - city:    string (required to trigger a lookup)
 *   - state:   string (2-letter USPS code, required to trigger a lookup)
 *   - onPick:  (zip: string) => void
 *   - currentZip?: string — if the user already typed a ZIP, we hide the
 *                          suggestions so we don't nudge them mid-typing.
 *   - testid?: string — override for the wrapper data-testid.
 */
import { useEffect, useState } from 'react';

const OPENDATA_URL = 'https://public.opendatasoft.com/api/explore/v2.1/catalog/datasets/georef-united-states-of-america-zc-point/records';

export default function ZipSuggestions({ city, state, onPick, currentZip = '', testid = 'zip-suggestions' }) {
  const [zips, setZips] = useState([]);

  useEffect(() => {
    const c = (city || '').trim();
    const s = (state || '').trim().toUpperCase();
    // Require both fields and a plausibly-complete city name (≥3 chars).
    if (!c || c.length < 3 || s.length !== 2) {
      setZips([]);
      return;
    }
    // If the user has already typed 5 digits, they've committed — hide.
    if (/^\d{5}$/.test((currentZip || '').trim())) {
      setZips([]);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const q = `stusps_code="${s}" AND search(usps_city,"${c.replace(/"/g, '')}")`;
        const url = `${OPENDATA_URL}?where=${encodeURIComponent(q)}&limit=5&select=${encodeURIComponent('zip_code,usps_city,stusps_code')}`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const list = (data.results || [])
          .map((r) => r.zip_code)
          .filter((z) => /^\d{5}$/.test(z));
        setZips(list.slice(0, 5));
      } catch {
        /* silent — form remains fully manual */
      }
    }, 400); // 400ms debounce; user typing "Dallas" won't fire 6 requests
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [city, state, currentZip]);

  if (zips.length === 0) return null;

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
        Suggested ZIP:
      </span>
      {zips.map((z) => (
        <button
          key={z}
          type="button"
          onClick={() => onPick(z)}
          className="px-2 py-0.5 rounded-full text-[11px] font-bold border transition hover:scale-[1.05]"
          style={{
            backgroundColor: 'rgba(201,169,98,0.12)',
            color: '#C9A962',
            borderColor: 'rgba(201,169,98,0.4)',
          }}
          data-testid={`${testid}-chip-${z}`}
        >
          {z}
        </button>
      ))}
    </div>
  );
}
